import * as React from 'react';
import {
  TextField,
  MenuItem,
  PrimaryButton,
  makeStyles,
  Theme,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  DefaultButton,
  useEnqueueBbSnackbar,
} from '@bb-ui/react-library';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { LoadingIndicator } from 'components/LoadingIndicator';
import { useRestApi } from 'hooks/useRestApi';
import { apiUrl } from 'utils/apiUrl';
import { useSnackbar } from 'hooks/useSnackbar';

const useStyles = makeStyles((theme: Theme) => ({
  card: {
    marginTop: theme.spacing(3),
    flexShrink: 0,
  },
  cardHeaderContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
  headerActions: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    marginLeft: 'auto',
  },
  savedQueryDropdown: {
    minWidth: '250px',
    backgroundColor: theme.palette.common.white,
  },
  savedQueryItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(0.5),
  },
  savedQueryName: {
    fontWeight: 500,
    fontSize: '0.875rem',
  },
  savedQueryPreview: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    fontFamily: 'monospace',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '220px',
  },
  formField: {
    marginBottom: theme.spacing(3),
  },
  sqlField: {
    fontFamily: 'monospace',
  },
  sitesContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    minHeight: '60px',
    maxHeight: '200px',
    overflowY: 'auto',
  },
  sitesLabel: {
    marginBottom: theme.spacing(1),
    fontWeight: 500,
  },
  emptySites: {
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
  },
  cardActions: {
    justifyContent: 'flex-end',
    padding: theme.spacing(2),
    gap: theme.spacing(2),
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  loadingMessage: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
  errorMessage: {
    color: theme.palette.error.main,
    fontSize: '0.875rem',
    marginRight: 'auto',
  },
}));

export interface SavedQuery {
  queryId: string;
  timestamp: number;
  queryInfo: {
    queryName: string;
    query: string;
    database: string;
    limitResult: number;
    queryTimeout: number;
  };
  tenantList: string[];
}

export interface MassQueryFormData {
  queryId?: string;
  queryName: string;
  sql: string;
  database: string;
  maxRecordsPerSite: number;
  timeoutSeconds: number;
  tenantList: string[];
}

interface MassQueryFormProps {
  selectedSites: Array<{ TenantId?: string; Url?: string }>;
  selectedRegion: string;
  setSelectedSites: (sites: Array<{ TenantId?: string; Url?: string }>) => void;
  onSuccess?: () => void;
}

export const MassQueryForm: React.FC<MassQueryFormProps> = ({
  selectedSites,
  setSelectedSites,
  selectedRegion,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const classes = useStyles();
  const history = useHistory();
  const [queryId, setQueryId] = React.useState('unknown');
  const [loadedSaveQuery, setLoadedSaveQuery] = React.useState<string>(
    'Select a saved query to load',
  );
  const isManualSaveRef = React.useRef(false);

  const {
    doPost: saveQuery,
    loadingRequests: savingQuery,
    failedRequests: failedSaving,
    succeededRequests: succeededSaving,
    clearSucceededRequests: clearSucceededSaving,
  } = useRestApi(apiUrl('lct', `mass-job/regions/${selectedRegion}/queries/${queryId}`), {
    manual: true,
  });

  const {
    data: savedQueries,
    loading: loadingSavedQueries,
    error: savedQueryError,
    fetch: fetchSavedQueries,
  } = useRestApi(apiUrl('lct', `mass-job/regions/${selectedRegion}/queries`));

  const { doPost, loadingRequests, failedRequests, succeededRequests, clearSucceededRequests } =
    useRestApi(apiUrl('lct', `mass-job/regions/${selectedRegion}/execute`), { manual: true });

  const [queryName, setQueryName] = React.useState('');
  const [sql, setSql] = React.useState('');
  const [database, setDatabase] = React.useState('BB');
  const [maxRecordsPerSite, setMaxRecordsPerSite] = React.useState(1000);
  const [timeoutSeconds, setTimeoutSeconds] = React.useState(30);

  const savedQueriesLoaded = React.useMemo<SavedQuery[]>(
    () => savedQueries?.queries ?? [],
    [savedQueries],
  );

  const executionIdRef = React.useRef<string | undefined>();

  const showSuccessSnackbar = useEnqueueBbSnackbar(
    { snackbarMessage: 'Query submitted successfully' },
    {
      actionMessage: 'View Executions',
      onActionClick: () => {
        const executionId = executionIdRef.current;
        if (executionId && selectedRegion) {
          history.push(`/query-tooling/regions/${selectedRegion}/executions/${executionId}`);
          closeSnackbar();
        }
      },
      closeButtonAriaLabel: 'close',
    },
  );

  React.useEffect(() => {
    if (succeededRequests.length > 0) {
      const result = succeededRequests.at(0);
      const executionId: string | undefined =
        result?.response?.data?.jobExecutionId || result?.response?.data?.executionId;
      executionIdRef.current = executionId;
      showSuccessSnackbar();
      setQueryName('');
      setSql('');
      setDatabase('BB');
      setMaxRecordsPerSite(1000);
      setTimeoutSeconds(30);
      clearSucceededRequests();
      setQueryId('unknown');
      setLoadedSaveQuery('Select a saved query to load');
      if (onSuccess) {
        onSuccess();
      }
    }
  }, [succeededRequests, clearSucceededRequests, onSuccess, showSuccessSnackbar]);

  React.useEffect(() => {
    if (succeededSaving.length === 0) return;

    if (isManualSaveRef.current) {
      enqueueSnackbar('Query saved successfully', { variant: 'success' });
      isManualSaveRef.current = false;
    }
    const timer = setTimeout(() => {
      clearSucceededSaving();
      fetchSavedQueries();
    }, 3000);
    return () => clearTimeout(timer);
  }, [succeededSaving, clearSucceededSaving, fetchSavedQueries, enqueueSnackbar]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSaveQuery();
    doPost({
      tenantList: selectedSites.map((site) => site.TenantId).filter((id): id is string => !!id),
      queryInfo: {
        queryName,
        database,
        query: sql,
        queryTimeout: timeoutSeconds,
        limitResult: maxRecordsPerSite,
      },
    });
  };

  const [firstFailedRequest] = failedRequests;
  const error = firstFailedRequest?.error ?? null;

  React.useEffect(() => {
    if (error) {
      enqueueSnackbar(`Error: ${error.message || 'An error occurred while submitting the query'}`, {
        variant: 'error',
      });
    }
  }, [error, enqueueSnackbar]);

  React.useEffect(() => {
    if (failedSaving.length > 0) {
      enqueueSnackbar(
        `Save Error: ${failedSaving[0].error?.message || 'An error occurred while saving the query'}`,
        { variant: 'error' },
      );
    }
  }, [failedSaving, enqueueSnackbar]);

  const isFormValid = queryName.trim() !== '' && sql.trim() !== '' && selectedSites.length > 0;

  const handleSaveQuery = (isManual = false) => {
    if (isManual) {
      isManualSaveRef.current = true;
    }
    saveQuery({
      tenantList: selectedSites.map((site) => site.TenantId).filter((id): id is string => !!id),
      queryInfo: {
        queryName,
        query: sql,
        database,
        limitResult: maxRecordsPerSite,
        queryTimeout: timeoutSeconds,
      },
    });
  };

  const handleLoadSavedQuery = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    try {
      const query: SavedQuery = JSON.parse(value);
      setQueryId(query.queryId!);
      setQueryName(query.queryInfo.queryName);
      setSql(query.queryInfo.query);
      setDatabase(query.queryInfo.database);
      setMaxRecordsPerSite(query.queryInfo.limitResult);
      setTimeoutSeconds(query.queryInfo.queryTimeout);
      setLoadedSaveQuery(query.queryInfo.queryName);
      setSelectedSites(query.tenantList.map((tenantId) => ({ TenantId: tenantId })));
      enqueueSnackbar('Successfully loaded query', { variant: 'success' });
    } catch {
      enqueueSnackbar('Failed to load query', { variant: 'error' });
    }
  };

  return (
    <Card className={classes.card} raised>
      <CardHeader
        title={t('massQueryTooling.runQuery')}
        subheader={
          <div className={classes.cardHeaderContent}>
            <div className={classes.headerActions}>
              {loadingSavedQueries && <LoadingIndicator size="small" />}
              {!loadingSavedQueries && savedQueryError && (
                <span style={{ color: 'red' }}>Failed to load saved queries</span>
              )}
              {!loadingSavedQueries && !savedQueryError && savedQueriesLoaded.length > 0 && (
                <TextField
                  select
                  id="saved-query-select"
                  value={loadedSaveQuery}
                  onChange={handleLoadSavedQuery}
                  className={classes.savedQueryDropdown}
                  placeholder="Load Saved Query"
                  SelectProps={{
                    renderValue: (value) => {
                      try {
                        const query: MassQueryFormData = JSON.parse(value as string);
                        return query.queryName;
                      } catch {
                        return value as string;
                      }
                    },
                  }}
                >
                  {savedQueriesLoaded.map((query) => (
                    <MenuItem key={query.queryId} value={JSON.stringify(query)}>
                      <div className={classes.savedQueryItem}>
                        <div className={classes.savedQueryName}>{query.queryInfo.queryName}</div>
                        <div className={classes.savedQueryPreview}>{query.queryInfo.query}</div>
                        <div className={classes.savedQueryPreview}>
                          {new Date(query.timestamp).toLocaleString()}
                        </div>
                      </div>
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </div>
          </div>
        }
      />
      <CardContent>
        <form onSubmit={handleSubmit}>
          <TextField
            id="query-name"
            label="Query Name"
            value={queryName}
            onChange={(e) => setQueryName(e.target.value)}
            fullWidth
            required
            className={classes.formField}
            helperText="Name to save the query and for using it later."
          />

          <TextField
            id="sql-query"
            label="SQL Query"
            value={sql}
            onChange={(e) => setSql(e.target.value)}
            fullWidth
            required
            multiline
            minRows={8}
            className={classes.formField}
            InputProps={{
              classes: { input: classes.sqlField },
            }}
            helperText="A select SQL query, the limit value will be added when the query runs."
          />

          <TextField
            select
            id="database"
            label="Database"
            value={database}
            onChange={(e) => setDatabase(e.target.value)}
            fullWidth
            className={classes.formField}
            helperText="Database where the sql will be executed."
          >
            <MenuItem value="BB">BB</MenuItem>
            <MenuItem value="BB_cms">BB_cms</MenuItem>
            <MenuItem value="BB_cms_doc">BB_cms_doc</MenuItem>
            <MenuItem value="BB_stats">BB_stats</MenuItem>
            <MenuItem value="BB_admin">BB_admin</MenuItem>
          </TextField>

          <TextField
            id="max-records"
            label="Max Records Per Site"
            type="number"
            value={maxRecordsPerSite}
            onChange={(e) => setMaxRecordsPerSite(Number(e.target.value))}
            fullWidth
            className={classes.formField}
            inputProps={{ min: 1, max: 1000000 }}
            helperText="Value between 1 and 1000000 records. The maximum records of the SQL query on each instance."
          />

          <TextField
            id="timeout"
            label="Timeout (seconds)"
            type="number"
            value={timeoutSeconds}
            onChange={(e) => setTimeoutSeconds(Number(e.target.value))}
            fullWidth
            className={classes.formField}
            inputProps={{ min: 1, max: 30 }}
            helperText="Value between 1 and 30 seconds. The time to wait for the execution of the SQL query."
          />
        </form>
      </CardContent>
      <CardActions className={classes.cardActions}>
        {loadingRequests ? (
          <div className={classes.loadingContainer}>
            <LoadingIndicator size="small" />
            <span className={classes.loadingMessage}>Submitting query...</span>
          </div>
        ) : (
          <>
            {savingQuery ? (
              <div className={classes.loadingContainer}>
                <LoadingIndicator size="small" />
                <span className={classes.loadingMessage}>Saving query...</span>
              </div>
            ) : (
              <DefaultButton onClick={() => handleSaveQuery(true)} disabled={!isFormValid}>
                {t('massQueryTooling.saveQuery')}
              </DefaultButton>
            )}
            <PrimaryButton onClick={handleSubmit} disabled={!isFormValid || loadingRequests}>
              {t('massQueryTooling.runQuery')}
            </PrimaryButton>
          </>
        )}
      </CardActions>
    </Card>
  );
};
